<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Card } from '@/components/ui/card'
import PageHeader from '@/components/PageHeader.vue'
import { useUser } from '@/composables/useUser'
import { useDateRangeStore } from '@/store/dateRangeStore'
import SummaryView from './components/SummaryView.vue'
import TenantView from './components/TenantView.vue'
import UserView from './components/UserView.vue'
import DrilldownView from './components/DrilldownView.vue'
import ExecutionDetailView from './components/ExecutionDetailView.vue'
import type { LifecycleState } from '@/models/MetricsSnapshot'

type ViewState =
  | { view: 'summary'; lifecycleState?: LifecycleState }
  | { view: 'tenant'; tenantId: string; tenantName: string; lifecycleState?: LifecycleState }
  | { view: 'user'; tenantId: string; tenantName: string; userId: string; userName: string; lifecycleState?: LifecycleState }
  | { view: 'drilldown'; tenantId: string; tenantName: string; userId: string; userName: string; status?: string; lifecycleState?: LifecycleState }
  | { view: 'execution-detail'; tenantId: string; tenantName: string; userId: string; userName: string; executionId: string; executionIds?: string[]; status?: string; lifecycleState?: LifecycleState }

const currentView = ref<ViewState>({ view: 'summary' })

const auth = useUser()
const dateRangeStore = useDateRangeStore()

const isGlobalAdmin = ref(true)

// --- Navigation callbacks ---

function onNavigateToTenant(payload: { tenantId: string; tenantName: string }) {
  currentView.value = {
    view: 'tenant',
    tenantId: payload.tenantId,
    tenantName: payload.tenantName,
    lifecycleState: currentView.value.lifecycleState,
  }
}

function onNavigateToUser(payload: {
  tenantId: string
  tenantName: string
  userId: string
  userName: string
}) {
  currentView.value = {
    view: 'user',
    tenantId: payload.tenantId,
    tenantName: payload.tenantName,
    userId: payload.userId,
    userName: payload.userName,
    lifecycleState: currentView.value.lifecycleState,
  }
}

function onNavigateToSummary() {
  if (isGlobalAdmin.value) {
    currentView.value = { view: 'summary', lifecycleState: currentView.value.lifecycleState }
  }
}

function onNavigateToDrilldown(payload: { status?: string }) {
  if (currentView.value.view === 'user' || currentView.value.view === 'execution-detail') {
    currentView.value = {
      view: 'drilldown',
      tenantId: currentView.value.tenantId,
      tenantName: currentView.value.tenantName,
      userId: currentView.value.userId,
      userName: currentView.value.userName,
      status: payload.status,
      lifecycleState: currentView.value.lifecycleState,
    }
  }
}

function onNavigateToExecutionDetail(payload: { executionId: string; executionIds?: string[] }) {
  if (currentView.value.view === 'drilldown') {
    currentView.value = {
      view: 'execution-detail',
      tenantId: currentView.value.tenantId,
      tenantName: currentView.value.tenantName,
      userId: currentView.value.userId,
      userName: currentView.value.userName,
      executionId: payload.executionId,
      executionIds: payload.executionIds,
      status: currentView.value.status,
      lifecycleState: currentView.value.lifecycleState,
    }
  }
}

function onNavigateBack() {
  if (currentView.value.view === 'execution-detail') {
    currentView.value = {
      view: 'drilldown',
      tenantId: currentView.value.tenantId,
      tenantName: currentView.value.tenantName,
      userId: currentView.value.userId,
      userName: currentView.value.userName,
      status: currentView.value.status,
      lifecycleState: currentView.value.lifecycleState,
    }
  } else if (currentView.value.view === 'drilldown') {
    currentView.value = {
      view: 'user',
      tenantId: currentView.value.tenantId,
      tenantName: currentView.value.tenantName,
      userId: currentView.value.userId,
      userName: currentView.value.userName,
      lifecycleState: currentView.value.lifecycleState,
    }
  } else if (currentView.value.view === 'user') {
    // Navigate back to tenant view
    currentView.value = {
      view: 'tenant',
      tenantId: currentView.value.tenantId,
      tenantName: currentView.value.tenantName,
      lifecycleState: currentView.value.lifecycleState,
    }
  } else if (currentView.value.view === 'tenant' && isGlobalAdmin.value) {
    // Navigate back to summary (only for global admins)
    currentView.value = { view: 'summary', lifecycleState: currentView.value.lifecycleState }
  }
}

function onUpdateLifecycleState(state: LifecycleState) {
  currentView.value = { ...currentView.value, lifecycleState: state }
}

function onNavigateToExecution(payload: { executionId: string }) {
  if (currentView.value.view === 'execution-detail') {
    currentView.value = {
      ...currentView.value,
      executionId: payload.executionId,
    }
  }
}

// --- Lifecycle ---

onMounted(async () => {
  await auth.initialize()

  // Set default 30-day date range (overriding the store's 60-day default)
  dateRangeStore.set30DayRange()

  // TenantAdmin users skip directly to their tenant view
  if (!auth.is_global_admin()) {
    isGlobalAdmin.value = false
    currentView.value = {
      view: 'tenant',
      tenantId: auth.user.tenant_id ?? '',
      tenantName: auth.user.tenant_name || auth.user.tenant_id || '',
    }
  }
})
</script>

<template>
  <PageHeader title="Administration - Tenant Metrics Dashboard" />

  <Card class="rounded-3xl p-6 dark:bg-[#0d1526]">
    <!-- Summary View (PlatformAdmin only) -->
    <SummaryView
      v-if="currentView.view === 'summary' && isGlobalAdmin"
      :lifecycleState="currentView.lifecycleState ?? 'all'"
      @navigate-to-tenant="onNavigateToTenant"
      @update:lifecycleState="onUpdateLifecycleState"
    />

    <!-- Tenant View -->
    <TenantView
      v-else-if="currentView.view === 'tenant'"
      :tenantId="currentView.tenantId"
      :tenantName="currentView.tenantName"
      :lifecycleState="currentView.lifecycleState ?? 'all'"
      @navigate-to-user="onNavigateToUser"
      @navigate-back="onNavigateBack"
      @update:lifecycleState="onUpdateLifecycleState"
    />

    <!-- User View -->
    <UserView
      v-else-if="currentView.view === 'user'"
      :tenantId="currentView.tenantId"
      :tenantName="currentView.tenantName"
      :userId="currentView.userId"
      :userName="currentView.userName"
      :lifecycleState="currentView.lifecycleState ?? 'all'"
      @navigate-to-summary="onNavigateToSummary"
      @navigate-to-tenant="onNavigateToTenant"
      @navigate-to-drilldown="onNavigateToDrilldown"
      @update:lifecycleState="onUpdateLifecycleState"
    />

    <!-- Drilldown View -->
    <DrilldownView
      v-else-if="currentView.view === 'drilldown'"
      :tenantId="currentView.tenantId"
      :tenantName="currentView.tenantName"
      :userId="currentView.userId"
      :userName="currentView.userName"
      :status="currentView.status"
      :lifecycleState="currentView.lifecycleState ?? 'all'"
      @navigate-to-summary="onNavigateToSummary"
      @navigate-to-tenant="onNavigateToTenant"
      @navigate-to-user="onNavigateToUser"
      @navigate-to-execution-detail="onNavigateToExecutionDetail"
      @update:lifecycleState="onUpdateLifecycleState"
    />

    <!-- Execution Detail View -->
    <ExecutionDetailView
      v-else-if="currentView.view === 'execution-detail'"
      :tenantId="currentView.tenantId"
      :tenantName="currentView.tenantName"
      :userId="currentView.userId"
      :userName="currentView.userName"
      :executionId="currentView.executionId"
      :executionIds="currentView.executionIds ?? []"
      :status="currentView.status"
      :lifecycleState="currentView.lifecycleState ?? 'all'"
      @navigate-to-summary="onNavigateToSummary"
      @navigate-to-tenant="onNavigateToTenant"
      @navigate-to-user="onNavigateToUser"
      @navigate-to-drilldown="onNavigateToDrilldown"
      @navigate-to-execution="onNavigateToExecution"
      @update:lifecycleState="onUpdateLifecycleState"
    />
  </Card>
</template>
